"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import { cn } from "@/lib/utils"

const slides = [
  {
    id: 1,
    title: "Step into Style: Your Ultimate Fashion Destination",
    subtitle: "50% OFF Summer Super Sale",
    description:
      "Discover the latest trends in fashion with our exclusive collection of premium clothing and accessories.",
    image: "https://cdn.pixabay.com/photo/2021/01/29/14/41/wardrobe-5961193_960_720.jpg?height=600&width=500",
    buttonText: "Shop Now",
  },
  {
    id: 2,
    title: "Elevate Your Wardrobe with Premium Quality",
    subtitle: "New Arrivals Collection",
    description: "Experience luxury fashion with our carefully curated selection of high-end clothing and accessories.",
    image: "https://cdn.pixabay.com/photo/2025/04/24/05/23/woman-9554464_640.jpg?height=600&width=500",
    buttonText: "Explore Collection",
  },
  {
    id: 3,
    title: "Fashion Forward: Trends That Define You",
    subtitle: "Limited Edition Pieces",
    description:
      "Stand out from the crowd with our exclusive limited edition fashion pieces designed for the modern individual.",
    image: "https://cdn.pixabay.com/photo/2019/08/07/07/05/woman-4390055_640.jpg?height=600&width=500",
    buttonText: "Shop Limited Edition",
  },
]

export function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length)
        setIsTransitioning(false)
      }, 300)
    }, 5000)

    return () => clearInterval(timer)
  }, [])

  const goToSlide = (index: number) => {
    if (index !== currentSlide) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentSlide(index)
        setIsTransitioning(false)
      }, 300)
    }
  }

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % slides.length)
  }

  const prevSlide = () => {
    goToSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1)
  }

  return (
    <section className="relative bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div
            className={cn(
              "space-y-8 transition-all duration-500",
              isTransitioning ? "opacity-0 transform translate-x-4" : "opacity-100 transform translate-x-0",
            )}
          >
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-400">
              <div className="w-6 h-6 bg-amber-900 dark:bg-amber-400 rounded flex items-center justify-center">
                <span className="text-white dark:text-gray-900 text-xs">%</span>
              </div>
              <span className="font-medium">{slides[currentSlide].subtitle}</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                {slides[currentSlide].title}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-md">{slides[currentSlide].description}</p>
            </div>

            <Button className="bg-gradient-to-r from-[#ea580c] to-[#d97706] hover:from-[#dc2626] hover:to-[#ea580c] text-white px-8 py-3 text-lg">
              {slides[currentSlide].buttonText} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <div className="relative">
            <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 rounded-full p-4 shadow-lg z-10">
              <div className="w-16 h-16 bg-amber-200 dark:bg-amber-300 rounded-full flex items-center justify-center">
                <span className="text-2xl">👓</span>
              </div>
            </div>

            <div
              className={cn(
                "transition-all duration-500 relative",
                isTransitioning ? "opacity-0 transform scale-95" : "opacity-100 transform scale-100",
              )}
            >
              <Image
                src={slides[currentSlide].image || "/placeholder.svg"}
                alt="Fashion Model"
                width={500}
                height={600}
                className="rounded-lg w-full h-[600px] object-cover"
              />

              {/* Next Slide Preview - Moved to bottom of image */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Next:</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {slides[(currentSlide + 1) % slides.length].title.split(":")[0]}...
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {slides.map((_, index) => (
                        <div
                          key={index}
                          className={cn(
                            "w-2 h-2 rounded-full transition-colors",
                            index === currentSlide ? "bg-amber-500" : "bg-gray-300 dark:bg-gray-600",
                          )}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <Button
              variant="outline"
              size="sm"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800"
              onClick={prevSlide}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800"
              onClick={nextSlide}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {slides.map((_, index) => (
            <button
              key={index}
              className={cn(
                "w-3 h-3 rounded-full transition-all duration-300",
                index === currentSlide
                  ? "bg-amber-900 dark:bg-amber-400"
                  : "bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500",
              )}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
